import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Save, RotateCcw, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError, fmtFJD } from "@/lib/api";

const SOURCE_LABELS = {
    db: { label: "DB Override", color: "bg-coral/20 text-coral border-coral/30" },
    env: { label: "Env Default", color: "bg-ocean/20 text-ocean dark:text-turquoise border-ocean/30 dark:border-turquoise/30" },
    fallback: { label: "Hardcoded Fallback", color: "bg-muted text-muted-foreground" },
};

export default function AdminPricingTab() {
    const [rows, setRows] = useState(null);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const [mcHealth, setMcHealth] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get("/admin/pricing");
            setRows(data.items || []);
            setEdits({});
        } catch (e) {
            toast.error("Failed to load pricing");
        }
    };

    const refreshMailchimp = async () => {
        try {
            const { data } = await api.get("/admin/mailchimp/health");
            setMcHealth(data);
        } catch (e) {
            setMcHealth({ enabled: false, error: "Could not reach health endpoint" });
        }
    };

    useEffect(() => { refresh(); refreshMailchimp(); }, []);

    const setEdit = (key, val) => setEdits((e) => ({ ...e, [key]: val }));

    const save = async () => {
        const payload = {};
        Object.entries(edits).forEach(([k, v]) => {
            if (v === "" || v === null || v === undefined) return;
            payload[k] = parseFloat(v);
        });
        if (Object.keys(payload).length === 0) {
            toast.error("Change at least one value first");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/admin/pricing", payload);
            toast.success("Pricing updated. New checkout sessions use the new amounts immediately.");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const clearOverride = async (key) => {
        setSaving(true);
        try {
            await api.patch("/admin/pricing", { [key]: null });
            toast.success("Override cleared. Reverted to env default.");
            refresh();
        } catch (err) {
            toast.error("Could not clear override");
        } finally {
            setSaving(false);
        }
    };

    if (rows === null) {
        return <Card className="p-8 text-center" data-testid="pricing-loading"><p className="font-app text-muted-foreground">Loading pricing…</p></Card>;
    }

    return (
        <div data-testid="pricing-tab" className="space-y-4">
            <Card className="p-5 bg-gradient-to-br from-coral/5 to-ocean/5 border-coral/20">
                <div className="flex items-start gap-3">
                    <Banknote className="h-6 w-6 text-coral mt-0.5 shrink-0" />
                    <div>
                        <h3 className="font-display text-lg font-bold mb-1">Marketplace pricing & fees</h3>
                        <p className="font-app text-sm text-muted-foreground">
                            Override the env-driven defaults for Joint Venture Premium and Boost listings without redeploying.
                            Changes take effect on the next checkout session created (no impact on already-paid txns).
                            Read precedence: <strong>DB Override</strong> → Env Default → Hardcoded Fallback.
                        </p>
                    </div>
                </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
                {rows.map((r) => {
                    const src = SOURCE_LABELS[r.source] || SOURCE_LABELS.fallback;
                    const editVal = edits[r.key] !== undefined ? edits[r.key] : (r.db_override_fjd ?? "");
                    const hasOverride = r.db_override_fjd !== null && r.db_override_fjd !== undefined;
                    return (
                        <Card key={r.key} className="p-5" data-testid={`pricing-card-${r.key}`}>
                            <div className="flex items-start justify-between gap-2 mb-3">
                                <div>
                                    <div className="font-display text-base font-bold">{r.label}</div>
                                    <div className="font-display text-3xl font-bold tabular-nums mt-1">
                                        {fmtFJD(r.effective_fjd)}
                                    </div>
                                </div>
                                <Badge className={`${src.color} text-[10px] uppercase tracking-wider border`}>
                                    {src.label}
                                </Badge>
                            </div>

                            <div className="space-y-2 mb-3 text-xs font-app text-muted-foreground">
                                <div className="flex justify-between">
                                    <span>Env default:</span>
                                    <span className="tabular-nums">{r.env_default_fjd != null ? fmtFJD(r.env_default_fjd) : "—"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Fallback:</span>
                                    <span className="tabular-nums">{fmtFJD(r.fallback_fjd)}</span>
                                </div>
                                {r.updated_at && (
                                    <div className="flex justify-between">
                                        <span>Last edited:</span>
                                        <span className="tabular-nums">{new Date(r.updated_at).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>

                            <Label className="font-app text-xs">Override (FJD)</Label>
                            <Input
                                data-testid={`pricing-input-${r.key}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={editVal}
                                onChange={(e) => setEdit(r.key, e.target.value)}
                                placeholder={`leave blank to use env default (${fmtFJD(r.env_default_fjd ?? r.fallback_fjd)})`}
                                className="mt-1"
                            />

                            {hasOverride && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => clearOverride(r.key)}
                                    disabled={saving}
                                    data-testid={`pricing-clear-${r.key}`}
                                    className="mt-2 text-xs h-8 text-coral hover:text-coral-deep"
                                >
                                    <RotateCcw className="h-3 w-3 mr-1" /> Clear override
                                </Button>
                            )}
                        </Card>
                    );
                })}
            </div>

            <div className="flex justify-end">
                <Button
                    onClick={save}
                    disabled={saving || Object.keys(edits).length === 0}
                    data-testid="pricing-save-btn"
                    className="bg-ocean-green text-white"
                >
                    <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save overrides"}
                </Button>
            </div>

            {/* Mailchimp health — live audience sync status */}
            <Card className="p-5" data-testid="mailchimp-health-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <Mail className="h-6 w-6 text-coral mt-0.5 shrink-0" />
                        <div>
                            <h3 className="font-display text-lg font-bold mb-1">Mailchimp audience sync</h3>
                            <p className="font-app text-xs text-muted-foreground">
                                Newsletter signups + investor-watchlist + new user registrations are pushed to your Mailchimp audience automatically.
                                Failures degrade gracefully (local DB always wins).
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={refreshMailchimp} className="font-app text-xs">
                        Re-check
                    </Button>
                </div>
                <div className="mt-4">
                    {mcHealth === null ? (
                        <div className="text-xs text-muted-foreground font-app">Checking…</div>
                    ) : !mcHealth.enabled ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground font-app">
                            <AlertCircle className="h-4 w-4" />
                            Mailchimp keys not configured — newsletter signups stay in DB only.
                        </div>
                    ) : mcHealth.ok ? (
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="bg-fiji-green/10 border border-fiji-green/30 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-fiji-green font-app font-bold mb-1 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" /> Connected
                                </div>
                                <div className="font-display text-sm font-bold truncate">{mcHealth.audience_name}</div>
                            </div>
                            <div className="bg-secondary/40 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app font-bold mb-1">Members</div>
                                <div className="font-display text-2xl font-bold tabular-nums">{mcHealth.member_count?.toLocaleString() ?? "—"}</div>
                            </div>
                            <div className="bg-secondary/40 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app font-bold mb-1">Server</div>
                                <div className="font-app text-sm font-mono">{mcHealth.server_prefix}</div>
                                <div className="font-app text-[10px] text-muted-foreground truncate">id: {mcHealth.audience_id}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-coral/10 border border-coral/30 rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-coral font-app font-bold mb-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" /> Connection failed
                            </div>
                            <div className="font-app text-xs text-coral-deep">{mcHealth.error}</div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
}
